import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Stack,
  Button,
  Group,
  Text,
  Box,
  Badge,
  Alert,
  useMantineTheme,
  Divider,
  Loader,
  ActionIcon,
} from '@mantine/core';
import { IconCheck, IconTrash, IconX, IconInfoCircle, IconRefresh } from '@tabler/icons';
import { showNotification } from '@mantine/notifications';
import { useAuth0 } from '@auth0/auth0-react';
import { validateTemplateField, TemplatePreviewResponse } from '../api/integrations2.0';
import { getGTMAppTemplatePreview } from '../api/internal-items';
import { TemplateInput } from './TemplateInput';

interface AvailableField {
  key: string;
  label: string;
  group: 'Original' | 'Mapped';
  type: string;
}

interface TemplateFieldSettingsBoxProps {
  field: any;
  availableFields: AvailableField[];
  onSave: (updatedField: any) => Promise<void>;
  onCancel: () => void;
  onDelete?: () => void;
  isSaving?: boolean;
  isDeleting?: boolean;
  // For API calls (Integrations 2.0)
  integrationKey?: string;
  object?: string;
  originObject?: string;
  // For API calls (GTM Apps)
  appType?: 'chrome-extension' | 'salesforce' | 'zendesk';
  itemType?: 'ideas' | 'evidences';
}

export const TemplateFieldSettingsBox: React.FC<TemplateFieldSettingsBoxProps> = ({
  field,
  availableFields,
  onSave,
  onCancel,
  onDelete,
  isSaving = false,
  isDeleting = false,
  integrationKey,
  object,
  originObject,
  appType,
  itemType,
}) => {
  const theme = useMantineTheme();
  const auth0 = useAuth0();
  const [template, setTemplate] = useState(field.template || '');
  const [hasChanges, setHasChanges] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previousTemplate, setPreviousTemplate] = useState<string>(field.template || '');

  // Extract field placeholders from template
  const extractFieldPlaceholders = useCallback((templateValue: string): string[] => {
    const matches = templateValue.match(/\{\{([^}]+)\}\}/g);
    return matches ? matches.map((m) => m.trim()) : [];
  }, []);

  // Fetch preview from backend
  const fetchPreview = useCallback(
    async (templateValue: string) => {
      if (!templateValue.trim()) {
        setPreview(null);
        return;
      }

      // Check if we have the required params for either Integrations 2.0 or GTM Apps
      const hasIntegrationsParams = integrationKey && object && originObject;
      const hasGTMAppsParams = appType && itemType;

      if (!hasIntegrationsParams && !hasGTMAppsParams) {
        setPreview(null);
        return;
      }

      setIsLoadingPreview(true);
      setPreviewError(null);

      try {
        let result: TemplatePreviewResponse;
        const fieldKey = field.key || field.fieldPath || field.name || field.fieldKey;

        if (hasIntegrationsParams) {
          // Integrations 2.0 API
          result = await validateTemplateField(
            integrationKey!,
            object!,
            originObject!,
            fieldKey,
            templateValue,
            auth0,
          );
        } else {
          // GTM Apps API
          result = await getGTMAppTemplatePreview(
            appType!,
            itemType!,
            fieldKey,
            templateValue,
            auth0,
          );
        }

        setPreview(result.preview);
      } catch (error: any) {
        console.error('[TemplatePreview] Error fetching template preview:', error);
        console.error('[TemplatePreview] Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });
        setPreviewError(error.message || 'Failed to load preview');
        setPreview(null);
      } finally {
        setIsLoadingPreview(false);
      }
    },
    [
      integrationKey,
      object,
      originObject,
      appType,
      itemType,
      field.key,
      field.fieldPath,
      field.name,
      field.fieldKey,
      auth0,
    ],
  );

  // Auto-refresh preview only when field placeholders are added or removed (with debounce)
  useEffect(() => {
    const currentFields = extractFieldPlaceholders(template);
    const previousFields = extractFieldPlaceholders(previousTemplate);

    // Check if fields were added or removed (compare field names, not full {{field}} strings)
    const currentFieldNames = currentFields.map((f) => f.replace(/\{\{|\}\}/g, '').trim());
    const previousFieldNames = previousFields.map((f) => f.replace(/\{\{|\}\}/g, '').trim());

    const fieldsChanged =
      currentFieldNames.length !== previousFieldNames.length ||
      currentFieldNames.some((fieldName) => !previousFieldNames.includes(fieldName)) ||
      previousFieldNames.some((fieldName) => !currentFieldNames.includes(fieldName));

    if (fieldsChanged) {
      // Fields changed, trigger preview refresh with debounce
      const timeoutId = setTimeout(() => {
        if (template) {
          fetchPreview(template);
        } else {
          setPreview(null);
        }
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    }

    // Always update previousTemplate to track changes
    setPreviousTemplate(template);
  }, [template, previousTemplate, extractFieldPlaceholders, fetchPreview]);

  // Initial preview load when component mounts or when API params change
  useEffect(() => {
    const hasIntegrationsParams = integrationKey && object && originObject;
    const hasGTMAppsParams = appType && itemType;
    if (template && (hasIntegrationsParams || hasGTMAppsParams)) {
      fetchPreview(template);
    }
  }, [integrationKey, object, originObject, appType, itemType]); // Re-fetch if API params change

  const handleTemplateChange = (value: string) => {
    setTemplate(value);
    setHasChanges(value !== (field.template || ''));
    // Note: Preview will auto-update via useEffect when fields are added/removed
  };

  const handleSave = async () => {
    if (!template.trim()) {
      showNotification({
        title: 'Template required',
        message: 'Please enter a template string',
        color: 'red',
      });
      return;
    }

    try {
      // Preserve newlines - only trim leading/trailing spaces/tabs, keep newlines
      // This removes leading/trailing whitespace but preserves all newlines
      const trimmedTemplate = template.replace(/^[ \t]+|[ \t]+$/gm, '');

      await onSave({
        ...field,
        template: trimmedTemplate,
        fieldType: 'template',
        type: field.type || 'text',
      });
      setHasChanges(false);
      showNotification({
        title: 'Template field updated',
        message: 'Template field saved successfully',
        color: 'green',
      });
    } catch (error) {
      console.error('Error saving template field:', error);
      showNotification({
        title: 'Error saving field',
        message: 'Failed to save template field',
        color: 'red',
      });
    }
  };

  const handleCancel = () => {
    setTemplate(field.template || '');
    setHasChanges(false);
    onCancel();
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    try {
      await onDelete();
      showNotification({
        title: 'Field deleted',
        message: 'Template field deleted successfully',
        color: 'green',
      });
    } catch (error) {
      console.error('Error deleting template field:', error);
      showNotification({
        title: 'Error deleting field',
        message: 'Failed to delete template field',
        color: 'red',
      });
    }
  };

  return (
    <Stack spacing="sm">
      {/* <Group position="apart">
        <Group spacing="xs">
          <Badge size="sm" color="teal">TEMPLATE</Badge>
          <Text size="sm" weight={500}>
            Template Field Configuration
          </Text>
        </Group>
      </Group>

      <Alert
        icon={<IconInfoCircle size={16} />}
        title="Template Fields"
        color="blue"
        variant="light"
      >
        <Text size="xs">
          Create dynamic text by combining static content with field placeholders. Type <code>{'{{'}</code> to insert a field.
        </Text>
      </Alert> */}

      <Box>
        <Text size="sm" weight={500} mb="xs">
          Template
        </Text>
        <TemplateInput
          value={template}
          onChange={handleTemplateChange}
          availableFields={availableFields}
          placeholder="Type {{ to insert a field... Example: Hello {{firstName}} {{lastName}}"
        />
      </Box>

      <Box>
        <Group position="apart" mb="xs">
          <Text size="sm" weight={500}>
            Preview
          </Text>
          <ActionIcon
            size="sm"
            variant="subtle"
            onClick={() => fetchPreview(template)}
            disabled={!template.trim() || isLoadingPreview}
            loading={isLoadingPreview}
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Group>
        {isLoadingPreview ? (
          <Box
            sx={{
              padding: '8px 12px',
              backgroundColor: theme.colors.gray[0],
              borderRadius: '4px',
              border: `1px solid ${theme.colors.gray[3]}`,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Loader size="xs" />
            <Text size="sm" style={{ fontStyle: 'italic', color: theme.colors.gray[7] }}>
              Loading preview...
            </Text>
          </Box>
        ) : previewError ? (
          <Alert color="red" variant="light">
            <Text size="xs">{previewError}</Text>
          </Alert>
        ) : preview ? (
          <Box
            sx={{
              padding: '8px 12px',
              backgroundColor: theme.colors.gray[0],
              borderRadius: '4px',
              border: `1px solid ${theme.colors.gray[3]}`,
            }}
          >
            <Text
              size="sm"
              style={{
                fontStyle: 'italic',
                color: theme.colors.gray[7],
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {preview}
            </Text>
          </Box>
        ) : (
          <Box
            sx={{
              padding: '8px 12px',
              backgroundColor: theme.colors.gray[0],
              borderRadius: '4px',
              border: `1px solid ${theme.colors.gray[3]}`,
            }}
          >
            <Text size="sm" style={{ fontStyle: 'italic', color: theme.colors.gray[5] }}>
              {template ? 'No sample data available' : 'Enter a template to see preview'}
            </Text>
          </Box>
        )}
      </Box>

      <Divider />

      <Group position="right" spacing="xs">
        {onDelete && (
          <Button
            variant="outline"
            color="red"
            size="sm"
            leftIcon={<IconTrash size={14} />}
            onClick={handleDelete}
            disabled={isDeleting}
            loading={isDeleting}
          >
            Delete Field
          </Button>
        )}
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={isSaving || isDeleting}
          leftIcon={<IconX size={14} />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || isSaving || isDeleting}
          loading={isSaving}
          leftIcon={<IconCheck size={14} />}
        >
          Save Changes
        </Button>
      </Group>
    </Stack>
  );
};
